import { FetchRequestAdapter } from "@microsoft/kiota-http-fetchlibrary";
import {
  AnonymousAuthenticationProvider,
  type RequestInformation,
} from "@microsoft/kiota-abstractions";
import { createAapWebOdataClientV1 } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Odata/V1/aapWebOdataClientV1";
import { createAapWebApiClient } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/aapWebApiClient";
import { getOktaAuth } from "./auth";
import { getConfig } from "./runtimeConfig";

/**
 * Custom authentication provider that uses Okta tokens
 */
class OktaAuthenticationProvider extends AnonymousAuthenticationProvider {
  constructor() {
    super();
    this.authenticateRequest = async (
      request: RequestInformation,
      _additionalAuthenticationContext?: Record<string, unknown>,
    ): Promise<void> => {
      const accessToken = await getOktaAuth().getOrRenewAccessToken();

      if (accessToken) {
        request.headers.tryAdd("Authorization", `Bearer ${accessToken}`);
      }
    };
  }
}

let odataApiClientInstance: ReturnType<typeof createAapWebOdataClientV1> | null = null;

/**
 * Initializes the OData API client with runtime configuration
 * Must be called after loadRuntimeConfig() has completed
 */
export function initializeODataApiClient() {
  if (odataApiClientInstance) {
    return odataApiClientInstance;
  }

  const config = getConfig();

  if (!config.VITE_API_HOST_VALUE) {
    throw new Error(
      "VITE_API_HOST_VALUE is not configured. " +
      "Please ensure config.json is properly deployed or .env.development is configured."
    );
  }

  const authProvider = new OktaAuthenticationProvider();
  const requestAdapter = new FetchRequestAdapter(authProvider);
  requestAdapter.baseUrl = `${config.VITE_API_HOST_VALUE}/Odata/V1`;

  odataApiClientInstance = createAapWebOdataClientV1(requestAdapter);
  return odataApiClientInstance;
}

/**
 * Gets the initialized OData API client instance
 * Throws an error if not initialized yet
 */
export function getODataApiClient() {
  if (!odataApiClientInstance) {
    throw new Error(
      "OData API client has not been initialized. " +
      "Call initializeODataApiClient() before accessing the client."
    );
  }
  return odataApiClientInstance;
}

let regularApiClientInstance: ReturnType<typeof createAapWebApiClient> | null = null;

/**
 * Initializes the regular API client with runtime configuration
 * Must be called after loadRuntimeConfig() has completed
 */
export function initializeRegularApiClient() {
  if (regularApiClientInstance) {
    return regularApiClientInstance;
  }

  const config = getConfig();

  if (!config.VITE_API_HOST_VALUE) {
    throw new Error(
      "VITE_API_HOST_VALUE is not configured. " +
      "Please ensure config.json is properly deployed or .env.development is configured."
    );
  }

  const authProvider = new OktaAuthenticationProvider();
  const requestAdapter = new FetchRequestAdapter(authProvider);
  requestAdapter.baseUrl = config.VITE_API_HOST_VALUE;

  regularApiClientInstance = createAapWebApiClient(requestAdapter);
  return regularApiClientInstance;
}

/**
 * Gets the initialized regular API client instance
 * Throws an error if not initialized yet
 */
export function getRegularApiClient() {
  if (!regularApiClientInstance) {
    throw new Error(
      "Regular API client has not been initialized. " +
      "Call initializeRegularApiClient() before accessing the client."
    );
  }
  return regularApiClientInstance;
}

/**
 * Legacy export for backward compatibility
 * @deprecated Use getODataApiClient() instead after calling initializeODataApiClient()
 */
export const apiClient = new Proxy({} as ReturnType<typeof createAapWebOdataClientV1>, {
  get(_target, prop) {
    return Reflect.get(getODataApiClient(), prop);
  }
});

/**
 * Legacy export for backward compatibility
 * @deprecated Use getRegularApiClient() instead after calling initializeRegularApiClient()
 */
export const regularApiClient = new Proxy({} as ReturnType<typeof createAapWebApiClient>, {
  get(_target, prop) {
    return Reflect.get(getRegularApiClient(), prop);
  }
});
